import React, { useEffect, useState } from "react"
import { usePersistedState } from "@/hooks/usePersistedState"
import { Link, useNavigate } from "react-router-dom"
import { api } from "@/lib/api"
import { PageHeader } from "@/components/ui/page-header"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader } from "@/components/ui/card"
import { CampaignStatusBadge } from "@/components/ui/status-badge"
import { EmptyState } from "@/components/ui/empty-state"
import { Progress } from "@/components/ui/progress"
import { Plus, Phone, MoreVertical, Users, CheckCircle, Clock, XCircle, CalendarOff, CalendarCheck, Pause, Play } from "lucide-react"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs"
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import { PostponeDialog } from "@/components/campaigns/PostponeDialog"
import { CreateCampaignDialog } from "@/components/campaigns/CreateCampaignDialog"
import { useToast } from "@/hooks/use-toast"
import { formatDateOnly, formatTimestamp, parseApiTimestamp } from "@/lib/datetime"
import { OrgPauseStrip } from "@/features/outreach"
import { useOrganizationsStore } from "@/stores/organizationsStore"

export default function CampaignsPage() {
  const navigate = useNavigate()
  const { toast } = useToast()
  const [campaigns, setCampaigns] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [filter, setFilter] = usePersistedState<string>("view:campaigns:status", "all")
  const [postponeDialogOpen, setPostponeDialogOpen] = useState(false)
  const [selectedCampaign, setSelectedCampaign] = useState<any | null>(null)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const { organizations, selectedOrganizationId } = useOrganizationsStore()
  const selectedOrg = organizations.find((o) => o.id === selectedOrganizationId)

  const fetchCampaigns = async () => {
    setLoading(true)
    try {
      const data = await api.getCampaignsWithStats()
      setCampaigns(data || [])
    } catch (error) {
      console.error("Failed to fetch campaigns:", error)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    fetchCampaigns()
  }, [])

  const handlePostpone = (campaign: any) => {
    setSelectedCampaign(campaign)
    setPostponeDialogOpen(true)
  }

  const handlePostponeConfirm = async (pauseStart: string, pauseEnd: string, reason?: string) => {
    if (!selectedCampaign) return

    try {
      await api.postponeCampaign(selectedCampaign.id, {
        pause_start: pauseStart,
        pause_end: pauseEnd,
        reason,
      })
      toast({
        title: "Campaign paused",
        description: `Calls will be paused from ${formatDateOnly(pauseStart, "MMM d")} to ${formatDateOnly(pauseEnd, "MMM d")}`,
      })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to pause campaign:", error)
      toast({
        title: "Failed to pause campaign",
        variant: "destructive",
      })
      throw error
    }
  }

  const handleClearPostpone = async (campaign: any) => {
    try {
      await api.clearCampaignPostpone(campaign.id)
      toast({ title: "Pause dates cleared" })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to clear pause dates:", error)
      toast({
        title: "Failed to clear pause dates",
        variant: "destructive",
      })
    }
  }

  const handlePauseIndefinitely = async () => {
    if (!selectedCampaign) return

    try {
      await api.pauseCampaign(selectedCampaign.id)
      toast({ title: "Campaign paused" })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to pause campaign:", error)
      toast({
        title: "Failed to pause campaign",
        variant: "destructive",
      })
      throw error
    }
  }

  const handleResumeCampaign = async (campaign: any) => {
    try {
      await api.resumeCampaign(campaign.id)
      toast({ title: "Campaign resumed" })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to resume campaign:", error)
      toast({
        title: "Failed to resume campaign",
        variant: "destructive",
      })
    }
  }

  const handleDeleteCampaign = async (campaign: any) => {
    if (!window.confirm(`Are you sure you want to delete "${campaign.name}"? This will archive the campaign.`)) return

    try {
      await api.deleteCampaign(campaign.id)
      toast({ title: "Campaign deleted" })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to delete campaign:", error)
      toast({ title: "Failed to delete campaign", variant: "destructive" })
    }
  }

  const handleCreateCampaign = async (data: { name: string; completionCriteria: string }) => {
    try {
      await api.createCampaign({
        name: data.name,
        workflow_type: "campaign",
        config: {
          type: "campaign",
          completion: { criteria: data.completionCriteria },
        },
      })
      toast({ title: "Campaign created" })
      await fetchCampaigns()
    } catch (error) {
      console.error("Failed to create campaign:", error)
      toast({ title: "Failed to create campaign", variant: "destructive" })
      throw error
    }
  }

  const isPostponed = (campaign: any) => {
    return campaign.pause_start && campaign.pause_end
  }

  const filteredCampaigns = campaigns.filter((campaign) => {
    if (campaign.status?.toLowerCase() === "archived") return false
    if (filter === "all") return true
    return campaign.status?.toLowerCase() === filter
  })

  const getProgressValue = (campaign: any) => {
    const total = campaign.enrolled_count || 0
    const completed = campaign.completed_count || 0
    if (total === 0) return 0
    return Math.round((completed / total) * 100)
  }

  return (
    <div className="mx-auto max-w-7xl min-w-0 space-y-4 p-4 md:space-y-6 md:p-8">
      <PageHeader
        title="Campaigns"
        description="Manage your patient engagement campaigns"
        actions={
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" onClick={() => setCreateDialogOpen(true)}>
              <Plus className="h-4 w-4 mr-2" />
              New Campaign
            </Button>
          </div>
        }
      />

      {selectedOrganizationId && selectedOrg && (
        <OrgPauseStrip orgId={selectedOrganizationId} orgName={selectedOrg.name} />
      )}

      <Tabs value={filter} onValueChange={setFilter}>
        <TabsList className="w-full justify-start overflow-x-auto md:w-auto">
          <TabsTrigger value="all">All</TabsTrigger>
          <TabsTrigger value="active">Active</TabsTrigger>
          <TabsTrigger value="paused">Paused</TabsTrigger>
          <TabsTrigger value="completed">Completed</TabsTrigger>
        </TabsList>
      </Tabs>

      {loading ? (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[1, 2, 3, 4].map((i) => (
            <Card key={i} className="animate-pulse">
              <CardHeader className="pb-2">
                <div className="h-5 w-48 bg-muted rounded" />
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="h-4 w-24 bg-muted rounded" />
                <div className="h-2 w-full bg-muted rounded" />
                <div className="grid grid-cols-4 gap-4">
                  {[1, 2, 3, 4].map((j) => (
                    <div key={j} className="space-y-1">
                      <div className="h-6 w-12 bg-muted rounded" />
                      <div className="h-3 w-16 bg-muted rounded" />
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      ) : filteredCampaigns.length === 0 ? (
        <EmptyState
          icon={Phone}
          title="No campaigns found"
          description={filter === "all" ? "Create your first campaign to start engaging with patients" : `No ${filter} campaigns`}
          action={
            filter === "all" && (
              <Button onClick={() => setCreateDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create Campaign
              </Button>
            )
          }
        />
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredCampaigns.map((campaign) => (
            <Card
              key={campaign.id}
              className="min-w-0 hover:shadow-md transition-shadow cursor-pointer"
              onClick={() => campaign.name === "APCM" ? navigate("/apcm-ops") : navigate(`/campaigns/${campaign.id}`)}
            >
              <CardHeader className="flex flex-row items-start justify-between gap-2 pb-2">
                <div className="min-w-0 space-y-1">
                  <Link
                    to={campaign.name === "APCM" ? "/apcm-ops" : `/campaigns/${campaign.id}`}
                    className="font-semibold hover:underline break-words"
                    onClick={(e) => e.stopPropagation()}
                  >
                    {campaign.name}
                  </Link>
                  <div className="flex items-center gap-2 flex-wrap">
                    <CampaignStatusBadge status={campaign.status} />
                    {isPostponed(campaign) && (
                      <Badge variant="outline" className="bg-warning/10 text-warning border-warning/30">
                        <CalendarOff className="h-3 w-3 mr-1" />
                        Paused {formatTimestamp(campaign.pause_start, "MMM d")} - {formatTimestamp(campaign.pause_end, "MMM d")}
                      </Badge>
                    )}
                    {campaign.started_at && !isPostponed(campaign) && (
                      <span className="text-xs text-muted-foreground">
                        Started {parseApiTimestamp(campaign.started_at)?.toLocaleDateString()}
                      </span>
                    )}
                  </div>
                </div>
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => navigate(`/campaigns/${campaign.id}`)}>
                      View Details
                    </DropdownMenuItem>
                    <DropdownMenuItem onSelect={() => navigate(`/campaigns/${campaign.id}?edit=true`)}>
                      Edit Campaign
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    {campaign.status?.toLowerCase() === "paused" ? (
                      <DropdownMenuItem onSelect={() => handleResumeCampaign(campaign)}>
                        <Play className="h-4 w-4 mr-2" />
                        Resume Campaign
                      </DropdownMenuItem>
                    ) : (
                      <DropdownMenuItem onSelect={() => handlePostpone(campaign)}>
                        <Pause className="h-4 w-4 mr-2" />
                        Pause Campaign
                      </DropdownMenuItem>
                    )}
                    {isPostponed(campaign) && (
                      <DropdownMenuItem onSelect={() => handleClearPostpone(campaign)}>
                        <CalendarCheck className="h-4 w-4 mr-2" />
                        Clear Pause Dates
                      </DropdownMenuItem>
                    )}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem className="text-destructive" onSelect={() => handleDeleteCampaign(campaign)}>
                      Delete Campaign
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-muted-foreground">Progress</span>
                    <span className="font-medium">{getProgressValue(campaign)}%</span>
                  </div>
                  <Progress value={getProgressValue(campaign)} className="h-2" />
                </div>

                <TooltipProvider>
                  <div className="grid grid-cols-4 gap-2 text-center sm:gap-4">
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="">
                          <div className="flex items-center justify-center gap-1">
                            <Users className="h-4 w-4 text-muted-foreground" />
                            <span className="font-semibold">{campaign.enrolled_count || 0}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Enrolled</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Total patients currently enrolled (active + paused)</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="">
                          <div className="flex items-center justify-center gap-1">
                            <Clock className="h-4 w-4 text-warning" />
                            <span className="font-semibold">{campaign.pending_count || 0}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">In Progress</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Patients actively being contacted</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="">
                          <div className="flex items-center justify-center gap-1">
                            <CheckCircle className="h-4 w-4 text-success" />
                            <span className="font-semibold">{campaign.completed_count || 0}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Completed</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Patients who completed the campaign</p>
                      </TooltipContent>
                    </Tooltip>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="">
                          <div className="flex items-center justify-center gap-1">
                            <XCircle className="h-4 w-4 text-destructive" />
                            <span className="font-semibold">{campaign.failed_count || 0}</span>
                          </div>
                          <p className="text-xs text-muted-foreground">Failed</p>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent>
                        <p>Patients who failed or were cancelled</p>
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>

                {campaign.today_scheduled && (
                  <p className="text-sm text-muted-foreground border-t pt-3">
                    Today: {campaign.today_completed || 0} of {campaign.today_scheduled} calls completed
                  </p>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Create Campaign Dialog */}
      <CreateCampaignDialog
        open={createDialogOpen}
        onOpenChange={setCreateDialogOpen}
        onConfirm={handleCreateCampaign}
      />

      {/* Pause/Postpone Dialog */}
      <PostponeDialog
        open={postponeDialogOpen}
        onOpenChange={setPostponeDialogOpen}
        title="Pause Campaign"
        description="Choose how you want to pause this campaign."
        onConfirm={handlePostponeConfirm}
        onPauseIndefinitely={handlePauseIndefinitely}
        showIndefiniteOption={true}
        currentPauseStart={selectedCampaign?.pause_start}
        currentPauseEnd={selectedCampaign?.pause_end}
        isPaused={selectedCampaign?.status === "paused"}
      />
    </div>
  )
}
